import React, { useEffect, useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { mergeClasses } from '@fluentui/react-components';
import { DataContext } from '@bingads-webui-campaign-react/ads-studio-shared-components';
import { AdsStudioSpinner } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { VideoTemplateEditorContextWrapper } from '@bingads-webui-campaign-react/video-ads-editor';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { CopilotRepositoryProvider } from '@bingads-webui/inline-copilot';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { Overlay, MessageBar, MessageBarType } from '@bingads-webui-react/fabric-control';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import {
  createVideoRepository,
  createImageRepository,
  createFolderRepository,
  uploadVideoMetadata,
} from '@bingads-webui-campaign-react/asset-library-services';
import { newVideoFromMTObject } from '@bingads-webui-campaign-react/image-experience-video-part';
import { useEditVideoPageStyles } from './style';
import { useVideoNotify } from './components/use-video-notify';

const EditVideoPage = (props) => {
  const {
    i18n,
    permissions,
    isCTV,
    logger,
    loadClipchampPlayer,
    pagePerfMarker,
    setDataLoaded,
    videoId,
    dataService,
    ...rest
  } = props;

  const {
    serverContext, preferenceService, cid, aid, oDataConfig, perfMarker, navigation,
  } = rest;
  const classes = useEditVideoPageStyles();
  const notify = useVideoNotify({ i18n, logger });
  const { getVideo } = dataService || {};
  const { dataPromise } = useContext(DataContext);
  const [videoMetadata, setVideoMetadata] = useState(null);
  const [finalUrl, setFinalUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [, setError] = useState();
  const activity = useContext(ActivityContext);

  const videoRepository = createVideoRepository({
    oDataConfig,
    aid,
    cid,
  });
  const folderRepository = createFolderRepository({
    i18n,
    logger,
    oDataConfig,
    aid,
    cid,
  });
  const imageRepository = createImageRepository({
    oDataConfig,
    aid,
    cid,
    logger,
    i18n,
    activity,
  });

  const assetLibraryConfig = {
    cid,
    aid,
    oDataConfig,
    dateFormat: _.get(serverContext, ['CurrentUser', 'CultureDateTimeFormat', 'ShortDatePattern']),
    timeZone: _.get(serverContext, ['CurrentAccount', 'TimeZone']),
    preferencesService: preferenceService,
    activityLogger: activity,
  };

  const logActivity = useCallback((message) => {
    if (activity && activity.signal) {
      activity.signal(message);
    }
  }, [activity]);

  useEffect(() => {
    const fetchDataAsync = async () => {
      try {
        pagePerfMarker.willFetchData();
        setIsLoading(true);
        if (videoId) {
          const video = await getVideo({ id: videoId, fetchMetadata: true });
          if (video && video.Metadata) {
            const videoData = JSON.parse(video.Metadata);
            setVideoMetadata(videoData);
            const templateId = videoData && videoData.template && videoData.template.templateId;
            logActivity(`[VideoAdsEditorPage] - Video Edit Loaded - TemplateId:${templateId}`);
          }
        } else {
          const result = await dataPromise; // Wait for the promise to resolve
          const videoRecommendation = result && result.data && result.data.ads && result.data.ads[0];
          setVideoMetadata(videoRecommendation);
          setFinalUrl(result && result.finalUrl);
          const templateId = videoRecommendation && videoRecommendation.template && videoRecommendation.template.templateId;
          logActivity(`[VideoAdsEditorPage] - Video Recommendation Loaded - TemplateId:${templateId}`);
        }
        setIsLoading(false);
      } catch (err) {
        setError(err);
        setIsLoading(false);
        if (logger) {
          logger.logTrace('Video Asset Gen - AdsStudio - Error', err);
        }
      }

      pagePerfMarker.willRender();
      if (setDataLoaded) {
        setDataLoaded();
      }
    };
    fetchDataAsync();
  }, [dataPromise, pagePerfMarker, setDataLoaded, logger, logActivity]);

  const onSaveVideo = useCallback(async (newMetadata) => {
    if (!newMetadata) {
      logger.logTrace('ads studio edit video page - no video data passed to save.');
    } else {
      setIsSaving(true);
      try {
        const uploadResult = await uploadVideoMetadata({
          videoMetadata: newMetadata,
          newVideoFromMTObject,
          folderRepository,
          imageRepository,
          videoRepository,
        });

        if (uploadResult && uploadResult.recommendationFormat === 'Clipchamp' && uploadResult.status === 'Draft') {
          logActivity('VideoAdsEditor - AdsStudio - Video Recommendation Saved');
          navigation.gotoVideosPage();
        } else {
          notify(false);
        }
      } catch (e) {
        logger.logTrace('ads studio edit video page - error while adding video.', e);
        notify(false);
      }
      setIsSaving(false);
    }
  }, [folderRepository, imageRepository, videoRepository, logger, navigation, notify, logActivity]);

  const onApply = useCallback((newMetadata) => {
    onSaveVideo(newMetadata);
  }, [onSaveVideo]);

  return (
    <div className={mergeClasses('ad-studio-video-edit-page', classes.editVideoPage)}>
      {(isLoading || isSaving) && (
        <Overlay styles={{ root: { zIndex: 1 } }}>
          <AdsStudioSpinner
            className="page-load-spinner"
            useCustomSize
            customStyle={{
              marginLeft: 250,
              marginTop: 250,
              marginRight: 250,
              marginBottom: 5,
              }}
          />
          {!isSaving && (
          <div className={classes.spinnerContent}>{videoId ? i18n.getString(_TL_('Loading...')) : i18n.getString(_TL_('Generating the video for you. This may take a few minutes...'))}</div>
          )}
        </Overlay>
      )}
      {
        (!isLoading) && videoMetadata && videoMetadata.template &&
        <div className={classes.editVideoContainer}>
          <VideoTemplateEditorContextWrapper
            i18n={i18n}
            isOpen
            videoMetaData={videoMetadata}
            onApply={onApply}
            setIsOpen={() => {}}
            isCTV={isCTV}
            cid={cid}
            aid={aid}
            finalUrl={finalUrl}
            assetLibraryConfig={assetLibraryConfig}
            permissions={permissions}
            isAADUser={serverContext && serverContext.IsAADUser}
            serverContext={serverContext}
            perfMarker={perfMarker && perfMarker.createChild('VideoTemplateEditor')}
            isDialog={false}
            loadClipchampPlayer={loadClipchampPlayer}
            showVideoTemplateSummaryBarControl
          />
        </div>
      }
      {
          !isLoading && (!videoMetadata || !videoMetadata.template) &&
          <div>
            <div className="message-bar">
              <MessageBar messageBarType={MessageBarType.error} >
                {i18n.getString(_TL_('An error occured while loading the video.'))}
              </MessageBar>
            </div>
          </div>
        }
    </div>
  );
};

EditVideoPage.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  isCTV: PropTypes.bool.isRequired,
  setDataLoaded: PropTypes.func.isRequired,
  pagePerfMarker: PropTypes.shape({
    willFetchData: PropTypes.func,
    willRender: PropTypes.func,
  }).isRequired,
};

EditVideoPage.defaultProps = {};

const EditVideoPageWithContexts = ({ ...props }) => {
  const serverContext = useContext(ServerContext);
  const { permissions } = props;
  const useClipchamp = permissions && permissions.dynamic && permissions.dynamic.VideoTemplateUI;

  return (
    <CopilotRepositoryProvider
      campaignId="-1"
      locale={serverContext.CurrentLocale}
      recommendationType="Video"
      serverContext={serverContext}
      useClipchamp={useClipchamp}
    >
      <EditVideoPage {...props} />
    </CopilotRepositoryProvider>);
};

EditVideoPageWithContexts.propTypes = {};

const WrappedComponent = withI18nConsumer(EditVideoPageWithContexts);

export { WrappedComponent as EditVideoPageComponent };

